// home/LaborWasteCalculator.jsx
import React, { useState } from 'react';
import { scrollToSection } from '../../utils/scrollToSection';

function Slider({ label, value, onChange, min, max, step, unit, prefix }) {
  return (
    <div className="mb-6">
      <div className="flex justify-between items-center mb-2">
        <label className="text-sm font-medium text-foreground">{label}</label>
        <span className="text-lg font-bold text-primary" style={{ fontFamily: "'Space Mono', monospace" }}>
          {prefix}{value.toLocaleString()}{unit}
        </span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full h-2 rounded-lg appearance-none cursor-pointer bg-border accent-primary"
      />
      <div className="flex justify-between text-xs text-muted-foreground mt-1">
        <span>{prefix}{min.toLocaleString()}{unit}</span>
        <span>{prefix}{max.toLocaleString()}{unit}</span>
      </div>
    </div>
  );
}

export function LaborWasteCalculator() {
  const [guards, setGuards] = useState(10);
  const [alertsPerDay, setAlertsPerDay] = useState(100);
  const [hourlyRate, setHourlyRate] = useState(35);

  const annualWaste = Math.round(
    (guards * hourlyRate) * (alertsPerDay * (5 / 60)) * 260
  );

  const formatted = annualWaste.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  });

  return (
    <div id="calculator" className="py-16 md:py-24 bg-muted">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-base text-accent-orange font-semibold tracking-wide uppercase">Stop Guessing</h2>
          <p className="mt-2 text-3xl leading-8 font-bold tracking-tight text-foreground sm:text-4xl" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Calculate Your Annual Labor Waste
          </p>
          <p className="mt-4 max-w-2xl text-lg text-muted-foreground mx-auto">
            Move the sliders to see the real cost of your passive SOC.
          </p>
        </div>

        <div className="max-w-4xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Sliders */}
            <div className="bg-card border border-border rounded-lg p-8">
              <Slider
                label="Number of Guards"
                value={guards}
                onChange={setGuards}
                min={1}
                max={200}
                step={1}
                unit=""
                prefix=""
              />
              <Slider
                label="Alerts per Day"
                value={alertsPerDay}
                onChange={setAlertsPerDay}
                min={1}
                max={500}
                step={1}
                unit=""
                prefix=""
              />
              <Slider
                label="Hourly Rate"
                value={hourlyRate}
                onChange={setHourlyRate}
                min={15}
                max={150}
                step={1}
                unit=""
                prefix="$"
              />
            </div>

            {/* Result */}
            <div className="bg-card border border-border rounded-lg p-8 flex flex-col items-center justify-center text-center">
              <p className="text-sm text-muted-foreground uppercase tracking-widest mb-4">Estimated Annual Labor Waste</p>
              <p className="text-5xl md:text-6xl font-bold text-accent-orange" style={{ fontFamily: "'Space Mono', monospace" }}>
                {formatted}
              </p>
              <p className="text-xs text-muted-foreground mt-4 max-w-xs">
                Based on 5 minutes average alert triage time, 260 working days per year
              </p>
              <button
                onClick={() => scrollToSection('audit')}
                className="mt-8 text-sm font-medium px-6 py-3 rounded-md bg-accent-orange text-white hover:bg-accent-orange/90 transition-colors"
              >
                See how Kinetic Guard eliminates this &rarr;
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
